import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AssetRequestError } from '../api/getAssetById';

export type AppErrorRecord = {
  at: string;
  source: string;
  message: string;
  request?: string;
  httpStatus?: number;
  httpStatusText?: string;
  errorCode?: string | number;
  errorDescription?: string;
  rawBody?: string;
  networkDetails?: string;
  /** Serialized request/context JSON when available. */
  contextJson?: string;
};

type ReportAppErrorInput = {
  source: string;
  message: string;
  request?: string;
  context?: unknown;
  assetError?: AssetRequestError;
};

type LastAppErrorContextValue = {
  lastError: AppErrorRecord | null;
  reportError: (input: ReportAppErrorInput) => void;
  clearError: () => void;
};

const LastAppErrorContext = createContext<LastAppErrorContextValue | null>(null);

function serializeContextJson(context: unknown): string | undefined {
  if (context === undefined || context === null) {
    return undefined;
  }
  try {
    return JSON.stringify(context, null, 2);
  } catch {
    return String(context);
  }
}

function formatRawBody(rawBody: string | undefined): string | undefined {
  const trimmed = rawBody?.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return trimmed;
  }
}

export function buildAppErrorRecord(input: ReportAppErrorInput): AppErrorRecord {
  const { assetError, context, message, request, source } = input;
  return {
    at: new Date().toISOString(),
    source,
    message,
    request,
    httpStatus: assetError?.httpStatus,
    httpStatusText: assetError?.httpStatusText,
    errorCode: assetError?.errorCode,
    errorDescription: assetError?.errorDescription,
    rawBody: formatRawBody(assetError?.rawBody),
    networkDetails: assetError?.networkDetails,
    contextJson: serializeContextJson(context),
  };
}

export function LastAppErrorProvider({ children }: { children: ReactNode }) {
  const [lastError, setLastError] = useState<AppErrorRecord | null>(null);

  const reportError = useCallback((input: ReportAppErrorInput) => {
    setLastError(buildAppErrorRecord(input));
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const value = useMemo(
    () => ({ lastError, reportError, clearError }),
    [lastError, reportError, clearError],
  );

  return <LastAppErrorContext.Provider value={value}>{children}</LastAppErrorContext.Provider>;
}

export function useLastAppError(): LastAppErrorContextValue {
  const ctx = useContext(LastAppErrorContext);
  if (!ctx) {
    throw new Error('useLastAppError must be used within LastAppErrorProvider');
  }
  return ctx;
}
